import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback, Fragment } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { AtlassianSupportLink } from '@confluence/error-boundary/entry-points/AtlassianSupportLink.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import { fireUIClickEvent } from '../AnalyticsEvents.tsx';
import ArchivedPageBannerV2ArchivedPageBannerQuery, {
	type ArchivedPageBannerV2ArchivedPageBannerQuery$data,
	type ArchivedPageBannerV2ArchivedPageBannerQuery$variables,
} from '../__generated__/ArchivedPageBannerV2ArchivedPageBannerQuery.graphql.ts';

import type {
	ArchivedPageBannerNoteCreateOrUpdateArchivePageNoteMutation as ArchivedPageBannerNoteCreateOrUpdateArchivePageNoteMutation$data,
	ArchivedPageBannerNoteCreateOrUpdateArchivePageNoteMutationVariables as ArchivedPageBannerNoteCreateOrUpdateArchivePageNoteMutation$variables,
} from './__types__/ArchivedPageBannerNoteCreateOrUpdateArchivePageNoteMutation.ts';
import { ArchivedPageBannerViewNote } from './ArchivedPageBannerViewNote.tsx';
import { ArchivedPageBannerEditNote } from './ArchivedPageBannerEditNote.tsx';
export const i18n = defineMessages({
	createNoteErrorTitle: {
		id: 'archive-pages.page-banner.note.creation.error.title',
		defaultMessage: 'We couldn’t add your note',
		description:
			'title for error flag when the action to add a note to an archived page/live doc fails',
	},
	updateNoteErrorTitle: {
		id: 'archive-pages.page-banner.note.update.error.title',
		defaultMessage: 'We couldn’t edit your note',
		description:
			'title for error flag when the action to update an existing note on an archived page fails',
	},
	createNoteErrorDescription: {
		id: 'archive-pages.page-banner.note.creation.error.description',
		defaultMessage:
			'Try again. If this keeps happening, contact {supportLink} or your space admin.',
		description:
			'description for error flag when the action to add a note to an archived page fails',
	},
	createNoteSuccessTitle: {
		id: 'archive-pages.page-banner.note.creation.success.title',
		defaultMessage: 'Note added',
		description:
			'title for success flag when the action to add a note to an archived page succeeds',
	},
	updateNoteSuccessTitle: {
		id: 'archive-pages.page-banner.note.update.success.title',
		defaultMessage: 'Note edited',
		description:
			'title for success flag when the action to update an existing note on an archived page succeeds',
	},
});

type ArchivedPageBannerNoteProps = {
	note: string;
	archiverAccountId?: string | null;
	contentId: string;
	editingNote: boolean;
	setEditingNote: (editingNote: boolean) => void;
	flags: FlagsStateContainer;
};

const ArchivedPageBannerNoteComponent: FC<ArchivedPageBannerNoteProps> = ({
	note,
	archiverAccountId,
	contentId,
	editingNote,
	setEditingNote,
	flags,
}) => {
	const { userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [createOrUpdateNote, { error: noteCreationError }] = useMutationInterop<
		ArchivedPageBannerNoteCreateOrUpdateArchivePageNoteMutation$data,
		ArchivedPageBannerNoteCreateOrUpdateArchivePageNoteMutation$variables
	>(
		graphql`
			mutation ArchivedPageBannerNoteCreateOrUpdateArchivePageNoteMutation(
				$pageId: Long!
				$archiveNote: String!
			) {
				createOrUpdateArchivePageNote(pageId: $pageId, archiveNote: $archiveNote)
			}
		`,
		{
			update: (_cache, { data: responseData }) => {
				const cachedData = readQueryInterop<
					ArchivedPageBannerV2ArchivedPageBannerQuery$data,
					ArchivedPageBannerV2ArchivedPageBannerQuery$variables
				>({
					query: ArchivedPageBannerV2ArchivedPageBannerQuery,
					variables: { contentId },
				});
				if (cachedData && responseData) {
					const copyOfCachedData = JSON.parse(JSON.stringify(cachedData));
					const contentNode = copyOfCachedData.content?.nodes?.[0];
					if (contentNode) {
						contentNode.archivedContentMetadata = {
							...contentNode.archivedContentMetadata,
							archiveNote: responseData.createOrUpdateArchivePageNote,
						};

						writeQueryInterop<
							ArchivedPageBannerV2ArchivedPageBannerQuery$data,
							ArchivedPageBannerV2ArchivedPageBannerQuery$variables
						>({
							query: ArchivedPageBannerV2ArchivedPageBannerQuery,
							variables: { contentId },
							data: copyOfCachedData,
						});
					}
				}
			},
			onError: () => {
				void flags.showErrorFlag({
					title: (
						<FormattedMessage {...(note ? i18n.updateNoteErrorTitle : i18n.createNoteErrorTitle)} />
					),
					description: (
						<FormattedMessage
							{...i18n.createNoteErrorDescription}
							values={{ supportLink: <AtlassianSupportLink /> }}
						/>
					),
				});
			},
		},
	);

	const userCanUpdateNote = userId === archiverAccountId;

	const openNoteCreation = useCallback(() => {
		setEditingNote(true);
	}, [setEditingNote]);

	const closeNoteCreation = useCallback(() => {
		setEditingNote(false);
	}, [setEditingNote]);

	const handleSaveNote = useCallback(
		(newArchiveNote: string) => {
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'button',
				actionSubjectId: !note ? 'addArchiveNoteSubmitButton' : 'updateArchiveNoteSubmitButton',
				source: 'archivePageBanner',
				attributes: {
					includedNote: !!newArchiveNote,
				},
			});

			void createOrUpdateNote({
				variables: {
					// Casting as `any` to match original types from ../__types__/CreateOrUpdateArchivePageNoteMutation
					// Ideally pageId should be typed as ID
					pageId: contentId as any,
					archiveNote: newArchiveNote,
				},
				optimisticResponse: {
					createOrUpdateArchivePageNote: newArchiveNote,
				},
			});

			closeNoteCreation();
		},
		[contentId, closeNoteCreation, createOrUpdateNote, createAnalyticsEvent, note],
	);

	const renderNote = () => {
		if (editingNote) {
			return (
				<ArchivedPageBannerEditNote
					defaultValue={note}
					onSave={handleSaveNote}
					onCancel={closeNoteCreation}
				/>
			);
		}

		return (
			<ArchivedPageBannerViewNote
				note={note}
				userCanUpdateNote={userCanUpdateNote}
				openNoteCreation={openNoteCreation}
			/>
		);
	};

	return (
		<Fragment>
			{renderNote()}
			{noteCreationError && <ErrorDisplay error={noteCreationError} />}
		</Fragment>
	);
};

export const ArchivedPageBannerNote = withTransparentErrorBoundary({
	attribution: Attribution.ADMIN_EXPERIENCE,
})(withFlags(ArchivedPageBannerNoteComponent));
